import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicMarqueeHeader from '../../components/PublicMarqueeHeader/PublicMarqueeHeader';
import PublicBottomNav from '../../components/PublicBottomNav/PublicBottomNav';
import ProfileSubNav from '../profile/ProfileSubNav';
import FriendsSubNav from '../friends/FriendsSubNav';
import EventsSubNav from '../events/EventsSubNav';
import { shouldShowEventsSubNav } from '../events/halloween/services/halloweenSeason';
import GameSubNav from '../game/GameSubNav';
import GameTopStats from '../game/GameTopStats';
import GameMemoryComposeSheet from '../game/components/GameMemoryComposeSheet';
import GameMemoryDetailSheet from '../game/components/GameMemoryDetailSheet';
import { GameShellProvider } from '../game/GameShellContext';
import useGameNavStats from '../game/hooks/useGameNavStats';
import { useAuth } from '../../contexts/AuthContext';
import { likedEventsList } from '../../services/userProfile';
import { createErrorLog } from '../../services/errorLogs';
import { syncEventDayReminders } from '../events/services/eventDayReminders';
import InstallAppOffer from './InstallAppOffer';
import useAppNotices from './useAppNotices';
import useRecordAppInstall from './useRecordAppInstall';
import { useTheme } from '../../contexts/ThemeContext';
import { countUnopenedPickups } from '../../services/exploreDrops';
// Parked Bucket Lists — restore unlock to mount Profile subnav on /sidequests.
// import { FEATURE_BUCKET_LISTS, isFeatureUnlocked } from '../../services/users/levelUnlocks';
// Gradual onboarding tips — disabled for now (restore import + wrap below)
// import { OnboardingTipsProvider } from './onboarding/OnboardingTipsProvider';
import '../../styles/public.css';

/**
 * Shared chrome for public routes: slim top bar + bottom nav.
 * Partner stays outside this layout. AdminHub mounts `PublicBottomNav` itself
 * so admins keep the public bottom tabs while using the internal tools shell.
 * Explore / Profile / Shop mount page-specific subnavs above the bottom nav (signed-in).
 * Dark Mode tokens apply on `.public-page[data-theme="dark"]` (paper family, not Game).
 */
const PublicLayout = () => {
  const { pathname } = useLocation();
  const { currentUser, userProfile } = useAuth();
  const { theme, darkModeOwned } = useTheme();
  const notices = useAppNotices();
  useRecordAppInstall();
  const reminderSyncUidRef = useRef('');
  const uid = currentUser?.uid || null;
  const signedIn = Boolean(currentUser) && !currentUser.isAnonymous;

  useEffect(() => {
    if (!uid || !userProfile || !signedIn) {
      if (!uid) reminderSyncUidRef.current = '';
      return undefined;
    }
    if (reminderSyncUidRef.current === uid) return undefined;
    reminderSyncUidRef.current = uid;
    syncEventDayReminders(uid, likedEventsList(userProfile)).catch((err) => {
      createErrorLog({
        message: err?.message || 'Could not sync event day reminders.',
        detail: err?.cause?.message || '',
        pageLabel: 'Events',
        source: 'event-day-reminder-sync',
        kind: 'error',
        uid,
        pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
        pageTitle: typeof document !== 'undefined' ? document.title : '',
      }).catch(() => {});
    });
    return undefined;
  }, [uid, userProfile, signedIn]);
  const isGameRoute =
    pathname === '/game' ||
    pathname.startsWith('/game/collection') ||
    pathname === '/explore' ||
    pathname.startsWith('/explore/');
  const isExploreBosna =
    pathname === '/explore-bosna' || pathname.startsWith('/explore-bosna/');
  const isVibecheck = pathname === '/vibecheck' || pathname === '/upload-vibecheck';
  const isProfile =
    pathname === '/profile' || pathname.startsWith('/profile/');
  const isShop = pathname === '/shop' || pathname.startsWith('/shop/');
  const isFriends = pathname === '/friends' || pathname.startsWith('/friends/');
  const isChatRoute = pathname === '/friends/chat' || pathname.startsWith('/friends/chat/');
  const isSidequests = pathname === '/sidequests' || pathname.startsWith('/sidequests/');
  const showGameSubNav = isGameRoute && signedIn;
  // const showBucketLists = isFeatureUnlocked(userProfile, FEATURE_BUCKET_LISTS);
  const showBucketLists = false;
  const showProfileSubNav =
    signedIn && (isProfile || isShop || (isSidequests && showBucketLists));
  const showFriendsSubNav = signedIn && isFriends;
  const showEventsSubNav = shouldShowEventsSubNav(pathname);
  const paperDark =
    darkModeOwned && theme === 'dark' && !isGameRoute && !isVibecheck;
  const unopenedCount = showGameSubNav ? countUnopenedPickups(userProfile) : 0;
  const navStats = useGameNavStats({
    uid,
    userProfile,
    enabled: showGameSubNav || showProfileSubNav,
  });
  const [composeOpen, setComposeOpen] = useState(false);
  const [memoryComposeOpen, setMemoryComposeOpen] = useState(false);
  const [activeMemory, setActiveMemory] = useState(null);
  const [memoryToast, setMemoryToast] = useState('');
  const onMine = pathname.startsWith('/profile/mine');

  useEffect(() => {
    if (!showGameSubNav && !onMine) {
      setComposeOpen(false);
      setMemoryComposeOpen(false);
    }
  }, [showGameSubNav, onMine]);

  useEffect(() => {
    if (!memoryToast) return undefined;
    const t = window.setTimeout(() => setMemoryToast(''), 2800);
    return () => window.clearTimeout(t);
  }, [memoryToast]);

  const exploreShell = useMemo(
    () => ({
      composeOpen,
      setComposeOpen,
      openCompose: () => {
        setMemoryComposeOpen(false);
        setComposeOpen(true);
      },
      closeCompose: () => setComposeOpen(false),
      memoryComposeOpen,
      openMemoryCompose: () => {
        setComposeOpen(false);
        setMemoryComposeOpen(true);
      },
      closeMemoryCompose: () => setMemoryComposeOpen(false),
      openMemoryDetail: (memory) => setActiveMemory(memory || null),
      closeMemoryDetail: () => setActiveMemory(null),
      activeMemory,
    }),
    [composeOpen, memoryComposeOpen, activeMemory]
  );

  return (
    <GameShellProvider value={exploreShell}>
      {/* <OnboardingTipsProvider> */}
        <div
          className={`public-page${
            isVibecheck ? '' : ' public-page--with-bottom-nav'
          }${
            showGameSubNav ? ' public-page--with-game-subnav public-page--with-explore-stats' : ''
          }${showProfileSubNav ? ' public-page--with-profile-subnav' : ''}${
            showFriendsSubNav ? ' public-page--with-friends-subnav' : ''
          }${showEventsSubNav ? ' public-page--with-events-subnav' : ''}${
            signedIn && isChatRoute ? ' public-page--chat' : ''
          }${
            isGameRoute ? ' public-page--game' : ''
          }${isExploreBosna ? ' public-page--explore-bosna' : ''}${
            isVibecheck ? ' public-page--vibecheck' : ''
          }`}
          data-theme={paperDark ? 'dark' : undefined}
        >
          <PublicNav
            showSectionLinks={false}
            notices={notices.notices}
            showInstallNotice={notices.showInstallNotice}
            noticeCount={notices.noticeCount}
            onDismissNotices={notices.dismiss}
            installOffer={<InstallAppOffer variant="panel" />}
          />
          {showGameSubNav ? (
            <GameTopStats
              pointsLabel={navStats.pointsLabel}
              exploredLabel={navStats.exploredLabel}
              dropsFoundLabel={navStats.dropsFoundLabel}
              dropsCreatedLabel={navStats.dropsCreatedLabel}
            />
          ) : null}
          {isGameRoute || isExploreBosna || isVibecheck || isChatRoute ? null : <PublicMarqueeHeader />}
          {isProfile ? (
            <div className="profile-theme-root">
              <Outlet />
            </div>
          ) : (
            <Outlet />
          )}
          {showProfileSubNav ? (
            <ProfileSubNav dropsCreated={navStats.dropsCreated} />
          ) : null}
          {showFriendsSubNav ? <FriendsSubNav /> : null}
          {showEventsSubNav ? <EventsSubNav /> : null}
          {showGameSubNav ? (
            <GameSubNav
              onAdd={() => {
                setMemoryComposeOpen(false);
                setComposeOpen(true);
              }}
              onAddMemory={() => {
                setComposeOpen(false);
                setMemoryComposeOpen(true);
              }}
              unopenedCount={unopenedCount}
              dropsFound={navStats.dropsFound}
              dropsStillOut={navStats.dropsStillOut}
            />
          ) : null}
          {isVibecheck ? null : (
            <>
              <InstallAppOffer
                raised={showFriendsSubNav || showGameSubNav || showProfileSubNav || showEventsSubNav}
              />
              <PublicBottomNav paperDark={paperDark} />
            </>
          )}
          {memoryToast ? (
            <p className="game-fog-page__toast" role="status">
              {memoryToast}
            </p>
          ) : null}
          {signedIn ? (
            <>
              <GameMemoryComposeSheet
                open={memoryComposeOpen}
                onClose={() => setMemoryComposeOpen(false)}
                uid={uid}
                friendIds={userProfile?.friendIds || []}
                onCreated={(memory) => {
                  setMemoryToast('Memory created — open for 24 hours.');
                  setActiveMemory(memory);
                }}
              />
              <GameMemoryDetailSheet
                memory={activeMemory}
                uid={uid}
                friendIds={userProfile?.friendIds || []}
                onClose={() => setActiveMemory(null)}
                onUpdated={setActiveMemory}
              />
            </>
          ) : null}
        </div>
      {/* </OnboardingTipsProvider> */}
    </GameShellProvider>
  );
};

export default PublicLayout;
